import React, { useCallback, useEffect, useMemo } from 'react';
import { AccountStatus, AutocompleteDropdown, Box, OptionKey } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { useGetEnrollments } from '@/modules/employee/Employee/hooks/useGetEnrollment';
import { DestinationType, LockboxValidationError, lockboxValidationErrorTitle } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

const SelectAccount = () => {
  const { editableLockbox, validationErrors, setEditableLockbox } = useLockboxDetailsStore();
  const employeeId = useMemo(() => editableLockbox?.claimRepayments?.[0]?.employeeId, [editableLockbox]);
  const { data, isLoading } = useGetEnrollments({ employeeId: employeeId });

  const openedAccounts = useMemo(() => data?.filter((account) => account.accountStatus === AccountStatus.OPEN), [data]);
  const accountOptions = useMemo(() => openedAccounts?.map((account) => ({
    key: toString(account?.id),
    value: toString(account?.id),
    title: account?.enrollmentTitle,
  })), [openedAccounts]);

  const handleChangeAccountId = useCallback((accountId: OptionKey) => {
    setEditableLockbox({
      remainder: {
        ...editableLockbox?.remainder,
        accountId: accountId as string
      },
    });
  }, [editableLockbox, setEditableLockbox]);

  // Initialization and state clearing
  useEffect(() => {
    setEditableLockbox({ remainder: { destinationType: DestinationType.PLAN_ACCOUNT } });
    return () => {
      setEditableLockbox({ remainder: undefined });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorType = useMemo(() => {
    return validationErrors?.find((error) => error === LockboxValidationError.ACCOUNT_REQUIRED) as LockboxValidationError | undefined;
  }, [validationErrors]);

  return (
    <Box margin={{ top: 'spacing12' }}>
      <AutocompleteDropdown
        testId="employee-account"
        name="employee-account"
        placeholder="Select account"
        value={editableLockbox?.remainder?.accountId}
        onChange={handleChangeAccountId}
        options={accountOptions}
        isLoading={isLoading}
        errorText={errorType ? lockboxValidationErrorTitle?.[errorType] : undefined}
      />
    </Box>
  );
};

export default SelectAccount;
